import type { DocumentsState } from './documents-store'
import type { Doc } from '../../types/Doc'

export function upsertDocumentInItems(items: Array<Doc>, document: Doc) {
  const documentIndex = items.findIndex((item) => item._id === document._id)

  if (documentIndex === -1) {
    return [...items, document]
  }

  return items.map((item, index) => (index === documentIndex ? document : item))
}

export function pruneNewDocumentIds({
  documentCollectionId,
  items,
  newDocumentIdsByCollectionId,
}: {
  documentCollectionId: string
  items: Array<Doc>
  newDocumentIdsByCollectionId: Record<string, Array<string>>
}) {
  const existingIds = new Set(items.map((item) => item._id))

  return (newDocumentIdsByCollectionId[documentCollectionId] ?? []).filter(
    (id) => existingIds.has(id),
  )
}

export function replaceDocumentsForCollection({
  documentCollectionId,
  items,
  nextDocuments,
}: {
  documentCollectionId: string
  items: Array<Doc>
  nextDocuments: Array<Doc>
}) {
  return [
    ...items.filter(
      (item) => item.documentCollectionId !== documentCollectionId,
    ),
    ...nextDocuments,
  ]
}

export function applyDocumentAddedState({
  document,
  state,
}: {
  document: Doc
  state: DocumentsState
}) {
  const documentExists = state.items.some((item) => item._id === document._id)

  return {
    items: documentExists
      ? upsertDocumentInItems(state.items, document)
      : prependDocument(state.items, document),
    newDocumentIdsByCollectionId: {
      ...state.newDocumentIdsByCollectionId,
      [document.documentCollectionId]: documentExists
        ? (state.newDocumentIdsByCollectionId[document.documentCollectionId] ??
          [])
        : [
            document._id,
            ...(
              state.newDocumentIdsByCollectionId[
                document.documentCollectionId
              ] ?? []
            ).filter((id) => id !== document._id),
          ],
    },
  }
}

export function applyDocumentRemovedState({
  documentCollectionId,
  documentId,
  state,
}: {
  documentCollectionId: string
  documentId: string
  state: DocumentsState
}) {
  return {
    items: state.items.filter((item) => item._id !== documentId),
    newDocumentIdsByCollectionId: {
      ...state.newDocumentIdsByCollectionId,
      [documentCollectionId]: (
        state.newDocumentIdsByCollectionId[documentCollectionId] ?? []
      ).filter((id) => id !== documentId),
    },
  }
}

export function applyDocumentRemovedByIdState({
  documentId,
  state,
}: {
  documentId: string
  state: DocumentsState
}) {
  const removedDocument = state.items.find((item) => item._id === documentId)

  if (!removedDocument) {
    return state
  }

  return applyDocumentRemovedState({
    documentCollectionId: removedDocument.documentCollectionId,
    documentId,
    state,
  })
}

function prependDocument(items: Array<Doc>, document: Doc) {
  return [document, ...items.filter((item) => item._id !== document._id)]
}
